import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import authService from "../services/authService";
import '../css/Register.css';
import { auth, googleProvider } from "../config/firebase";
import { signInWithPopup } from "firebase/auth";
import verificationService from "../services/verificationService";

const Register = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [username, setUsername] = useState("");
    const [smartBillEntry, setSmartBillEntry] = useState("");
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("info");
    const [errors, setErrors] = useState([]);
    const [showPassword, setShowPassword] = useState(false);
    const [showVerifyPrompt, setShowVerifyPrompt] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const emailParam = queryParams.get("email");

        if (emailParam) {
            setEmail(emailParam);
            setUsername(emailParam.split('@')[0]);
        }
    }, [location.search]);

    const handleRegister = async (e) => {
        e.preventDefault();
        setErrors([]);
        setMessage("");
        setShowVerifyPrompt(false);
        try {
            await authService.register(email, password, username, smartBillEntry);
            await verificationService.sendVerificationCode(email);
            setMessage("Registration successful! Please check your email for a verification code.");
            setMessageType("success");
            navigate(`/verify-email-code?email=${encodeURIComponent(email)}`);
        } catch (error) {
            let errorArr = [];
            if (error.errors && Array.isArray(error.errors)) {
                errorArr = error.errors
                    .filter(e => (e.path === 'password' && e.msg) || (typeof e === 'string' && e.toLowerCase().includes('password')))
                    .map(e => (typeof e === 'string' ? e : e.msg));
                if (errorArr.length === 0) {
                    errorArr = error.errors.map(e => (typeof e === 'string' ? e : e.msg || JSON.stringify(e)));
                }
            }
            if (error.status === 409 || (errorArr && errorArr.some(e => typeof e === 'string' && e.includes('not verified')))) {
                setShowVerifyPrompt(true);
                setErrors(["Email already registered but not verified. Please verify your email to continue."]);
            } else if (errorArr && errorArr.length > 0) {
                setErrors(errorArr);
            } else if (error.message) {
                setErrors([typeof error.message === 'string' ? error.message : JSON.stringify(error.message)]);
            } else {
                setErrors(["Registration failed"]);
            }
        }
    };

    const handleFirebaseGoogleLogin = async () => {
        try {
            setMessage("Connecting to Google...");
            setMessageType("info");
            const result = await signInWithPopup(auth, googleProvider);
            const user = result.user;
            setMessage("Google authentication successful, verifying with server...");
            const idToken = await user.getIdToken();
            await authService.handleFirebaseGoogleAuth({
                idToken,
                email: user.email,
                displayName: user.displayName,
                photoURL: user.photoURL,
                smartBillEntry
            });
            navigate("/members");
        } catch (error) {
            console.error("Error during Google login:", error);
            setMessage(`Google login failed: ${error.message}`);
            setMessageType("error");
        }
    };

    return (
        <div className="auth-page">
            <div className="auth-container">
                <form onSubmit={handleRegister} className="form-container">
                    <div className="auth-logo">
                        📊
                    </div>
                    <h2>Create Account</h2>
                    <p className="auth-subtitle">Join Autofolio to manage your invoices</p>

                    <div className="input-group has-label">
                        <label htmlFor="email">Email Address</label>
                        <span className="input-icon">📧</span>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            placeholder="Enter your email"
                        />
                    </div>

                    <div className="input-group has-label">
                        <label htmlFor="username">Username</label>
                        <span className="input-icon">👤</span>
                        <input
                            type="text"
                            id="username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            placeholder="Choose a username"
                        />
                    </div>

                    <div className="input-group has-label">
                        <label htmlFor="password">Password</label>
                        <span className="input-icon">🔒</span>
                        <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            placeholder="Create a password"
                        />
                        <button
                            type="button"
                            className="password-toggle"
                            tabIndex={-1}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            onClick={() => setShowPassword(v => !v)}
                        >
                            {showPassword ? "👁️" : "👁️‍🗨️"}
                        </button>
                    </div>

                    <div className="input-group has-label">
                        <label htmlFor="smartbill">Smart Bill Email (Optional)</label>
                        <span className="input-icon">📑</span>
                        <input
                            type="text"
                            id="smartbill"
                            value={smartBillEntry}
                            onChange={(e) => setSmartBillEntry(e.target.value)}
                            placeholder="Smart Bill forwarding email"
                        />
                    </div>

                    <button type="submit" className="submit-btn">
                        Create Account
                    </button>

                    <div className="separator">
                        <span>OR</span>
                    </div>

                    <button
                        type="button"
                        onClick={handleFirebaseGoogleLogin}
                        className="google-auth-btn"
                    >
                        Register with Google
                    </button>

                    {errors.length > 0 && (
                        <div className="error-message">
                            <strong>Registration Error{errors.length > 1 ? 's' : ''}:</strong>
                            <ul className="error-list">
                                {errors.map((err, idx) => (
                                    <li key={idx}>
                                        {err.replace('forwardEmail', 'Smart Bill Email').replace(/password.*must/i, 'Password must')}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {message && !errors.length && (
                        <p className={messageType === "error" ? "error-message" : messageType === "success" ? "success-message" : "info-message"}>
                            {message}
                        </p>
                    )}

                    {showVerifyPrompt && (
                        <button
                            type="button"
                            className="verify-prompt-btn"
                            onClick={() => navigate(`/verify-email-code?email=${encodeURIComponent(email)}`)}
                        >
                            Go to Email Verification
                        </button>
                    )}

                    <div className="auth-footer">
                        Already have an account? <Link to="/login">Sign in</Link>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Register;
